import React from 'react';

export interface LucideIconProps extends React.SVGProps<SVGSVGElement> {
  size?: number | string;
  strokeWidth?: number | string;
}

type IconNode = [keyof JSX.IntrinsicElements, Record<string, string | number>];

const createIcon = (name: string, iconNode: IconNode[]) => {
  const Icon = React.forwardRef<SVGSVGElement, LucideIconProps>(
    ({ color = 'currentColor', size = 24, strokeWidth = 2, className, ...props }, ref) => (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
      >
        {iconNode.map(([tag, attrs], index) =>
          React.createElement(tag, { key: `${name}-${index}`, ...attrs })
        )}
      </svg>
    )
  );

  Icon.displayName = name;
  return Icon;
};

export const Search = createIcon('Search', [
  ['circle', { cx: 11, cy: 11, r: 7 }],
  ['line', { x1: 21, y1: 21, x2: 16.65, y2: 16.65 }],
]);

export const List = createIcon('List', [
  ['line', { x1: 8, y1: 6, x2: 21, y2: 6 }],
  ['line', { x1: 8, y1: 12, x2: 21, y2: 12 }],
  ['line', { x1: 8, y1: 18, x2: 21, y2: 18 }],
  ['line', { x1: 3, y1: 6, x2: 5, y2: 6 }],
  ['line', { x1: 3, y1: 12, x2: 5, y2: 12 }],
  ['line', { x1: 3, y1: 18, x2: 5, y2: 18 }],
]);

export const LayoutGrid = createIcon('LayoutGrid', [
  ['rect', { x: 3, y: 3, width: 7, height: 7, rx: 1 }],
  ['rect', { x: 14, y: 3, width: 7, height: 7, rx: 1 }],
  ['rect', { x: 14, y: 14, width: 7, height: 7, rx: 1 }],
  ['rect', { x: 3, y: 14, width: 7, height: 7, rx: 1 }],
]);

export const ArrowUp = createIcon('ArrowUp', [
  ['line', { x1: 12, y1: 19, x2: 12, y2: 5 }],
  ['polyline', { points: '5 12 12 5 19 12' }],
]);

export const ArrowDown = createIcon('ArrowDown', [
  ['line', { x1: 12, y1: 5, x2: 12, y2: 19 }],
  ['polyline', { points: '19 12 12 19 5 12' }],
]);

export const MapPin = createIcon('MapPin', [
  ['path', { d: 'M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 1 1 18 0z' }],
  ['circle', { cx: 12, cy: 10, r: 3 }],
]);

export const BedDouble = createIcon('BedDouble', [
  ['path', { d: 'M4 14v4' }],
  ['path', { d: 'M20 14v4' }],
  ['rect', { x: 4, y: 8, width: 16, height: 6, rx: 2 }],
  ['path', { d: 'M10 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v2' }],
  ['path', { d: 'M20 8V6a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v2' }],
  ['path', { d: 'M2 18h20' }],
]);

export const Bath = createIcon('Bath', [
  ['path', { d: 'M3 12v4a5 5 0 0 0 5 5h8a5 5 0 0 0 5-5v-4' }],
  ['path', { d: 'M3 12h18' }],
  ['path', { d: 'M5 12V7a2 2 0 0 1 4 0v5' }],
  ['line', { x1: 2, y1: 12, x2: 2, y2: 12 }],
]);

export const Armchair = createIcon('Armchair', [
  ['path', { d: 'M5 12V9a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v3' }],
  ['path', { d: 'M2 12v4a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4v-4' }],
  ['path', { d: 'M6 12v4' }],
  ['path', { d: 'M18 12v4' }],
]);

export const ArrowLeft = createIcon('ArrowLeft', [
  ['line', { x1: 19, y1: 12, x2: 5, y2: 12 }],
  ['polyline', { points: '12 19 5 12 12 5' }],
]);

export const Pencil = createIcon('Pencil', [
  ['path', { d: 'M12 20h9' }],
  ['path', { d: 'M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z' }],
]);

export const Save = createIcon('Save', [
  ['path', { d: 'M17 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10l4-4V5a2 2 0 0 0-2-2Z' }],
  ['path', { d: 'M17 3v6H7V3' }],
  ['path', { d: 'M7 17h6' }],
]);

export const X = createIcon('X', [
  ['line', { x1: 18, y1: 6, x2: 6, y2: 18 }],
  ['line', { x1: 6, y1: 6, x2: 18, y2: 18 }],
]);

export const XCircle = createIcon('XCircle', [
  ['circle', { cx: 12, cy: 12, r: 10 }],
  ['line', { x1: 15, y1: 9, x2: 9, y2: 15 }],
  ['line', { x1: 9, y1: 9, x2: 15, y2: 15 }],
]);

export const ExternalLink = createIcon('ExternalLink', [
  ['path', { d: 'M18 3h3v3' }],
  ['path', { d: 'M10 14 21 3' }],
  ['path', { d: 'M21 10v11H3V3h11' }],
]);

export const CheckCircle = createIcon('CheckCircle', [
  ['circle', { cx: 12, cy: 12, r: 10 }],
  ['path', { d: 'm9 12 2 2 4-4' }],
]);

export const Clock = createIcon('Clock', [
  ['circle', { cx: 12, cy: 12, r: 9 }],
  ['polyline', { points: '12 7 12 12 15 15' }],
]);

export const AlertTriangle = createIcon('AlertTriangle', [
  ['path', { d: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z' }],
  ['line', { x1: 12, y1: 9, x2: 12, y2: 13 }],
  ['line', { x1: 12, y1: 17, x2: 12.01, y2: 17 }],
]);

export const Bug = createIcon('Bug', [
  ['rect', { x: 7, y: 8, width: 10, height: 8, rx: 3 }],
  ['path', { d: 'M12 2v4' }],
  ['path', { d: 'm5 5 2 2' }],
  ['path', { d: 'm19 5-2 2' }],
  ['path', { d: 'M3 13h2' }],
  ['path', { d: 'M3 17h2' }],
  ['path', { d: 'M19 13h2' }],
  ['path', { d: 'M19 17h2' }],
]);

export const Play = createIcon('Play', [
  ['polygon', { points: '6 4 20 12 6 20 6 4' }],
]);

export const Trash2 = createIcon('Trash2', [
  ['polyline', { points: '3 6 5 6 21 6' }],
  ['path', { d: 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6' }],
  ['path', { d: 'M10 11v6' }],
  ['path', { d: 'M14 11v6' }],
  ['path', { d: 'M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2' }],
]);

export const RefreshCw = createIcon('RefreshCw', [
  ['polyline', { points: '23 4 23 10 17 10' }],
  ['polyline', { points: '1 20 1 14 7 14' }],
  ['path', { d: 'M3.51 9a9 9 0 0 1 14.13-3.36L23 10' }],
  ['path', { d: 'M20.49 15a9 9 0 0 1-14.13 3.36L1 14' }],
]);

export const ChevronDown = createIcon('ChevronDown', [
  ['polyline', { points: '6 9 12 15 18 9' }],
]);

export const ChevronLeft = createIcon('ChevronLeft', [
  ['polyline', { points: '15 18 9 12 15 6' }],
]);

export const Menu = createIcon('Menu', [
  ['line', { x1: 3, y1: 6, x2: 21, y2: 6 }],
  ['line', { x1: 3, y1: 12, x2: 21, y2: 12 }],
  ['line', { x1: 3, y1: 18, x2: 21, y2: 18 }],
]);

export const Settings = createIcon('Settings', [
  ['circle', { cx: 12, cy: 12, r: 3 }],
  ['path', { d: 'M19.4 15a1.65 1.65 0 0 0 .33 1.82 2 2 0 1 1-2.83 2.83 1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.17a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33 2 2 0 1 1-2.83-2.83 1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.29a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82 2 2 0 1 1 2.83-2.83 1.65 1.65 0 0 0 1.82.33H10a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.17a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33 2 2 0 1 1 2.83 2.83 1.65 1.65 0 0 0-.33 1.82V10a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.29a1.65 1.65 0 0 0-1.31 0Z' }],
]);

export const Plus = createIcon('Plus', [
  ['line', { x1: 12, y1: 5, x2: 12, y2: 19 }],
  ['line', { x1: 5, y1: 12, x2: 19, y2: 12 }],
]);

export const Copy = createIcon('Copy', [
  ['rect', { x: 8, y: 8, width: 12, height: 12, rx: 2 }],
  ['path', { d: 'M4 16V6a2 2 0 0 1 2-2h10' }],
]);

export const ClipboardPaste = createIcon('ClipboardPaste', [
  ['rect', { x: 7, y: 4, width: 10, height: 16, rx: 2 }],
  ['path', { d: 'M9 2h6v4H9z' }],
  ['path', { d: 'M12 11v6' }],
  ['path', { d: 'M9 14h6' }],
]);

export const GripVertical = createIcon('GripVertical', [
  ['circle', { cx: 9, cy: 5, r: 1 }],
  ['circle', { cx: 9, cy: 12, r: 1 }],
  ['circle', { cx: 9, cy: 19, r: 1 }],
  ['circle', { cx: 15, cy: 5, r: 1 }],
  ['circle', { cx: 15, cy: 12, r: 1 }],
  ['circle', { cx: 15, cy: 19, r: 1 }],
]);

export const LogOut = createIcon('LogOut', [
  ['path', { d: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' }],
  ['polyline', { points: '16 17 21 12 16 7' }],
  ['line', { x1: 21, y1: 12, x2: 9, y2: 12 }],
]);

export const LayoutDashboard = createIcon('LayoutDashboard', [
  ['rect', { x: 3, y: 3, width: 8, height: 8, rx: 2 }],
  ['rect', { x: 13, y: 3, width: 8, height: 5, rx: 2 }],
  ['rect', { x: 13, y: 10, width: 8, height: 11, rx: 2 }],
  ['rect', { x: 3, y: 13, width: 8, height: 8, rx: 2 }],
]);

export const Database = createIcon('Database', [
  ['ellipse', { cx: 12, cy: 5, rx: 9, ry: 3 }],
  ['path', { d: 'M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5' }],
  ['path', { d: 'M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3' }],
]);

export const Home = createIcon('Home', [
  ['path', { d: 'M3 10.5 12 3l9 7.5V21a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1Z' }],
]);

export const Code = createIcon('Code', [
  ['polyline', { points: '16 18 22 12 16 6' }],
  ['polyline', { points: '8 6 2 12 8 18' }],
]);

export const Filter = createIcon('Filter', [
  ['path', { d: 'M4 4h16l-6 7v5l-4 4v-9Z' }],
]);

export const ExternalLinkSquare = ExternalLink;
